import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PointerSensor, useSensor, useSensors,
  pointerWithin, rectIntersection, closestCorners
} from "@dnd-kit/core";
import { submitSingleNextStage } from "../../../applicant/Axios";
import {
  ACCEPTED, canMove, dispatchClick, groupByStage, groupByStageProjected,
  preserveOrder, stageOf
} from "../helpers";

export default function useKanbanBoardState({
                                              hits, workFlow, courseId, reloadSearch, showNotification, switchToList, boardRef
                                            }) {
  // steps y sensores
  const steps = useMemo(() => [ACCEPTED, ...workFlow], [workFlow]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint:{ distance:5 } })
  );

  // estado base
  const [cols, setCols] = useState(() => groupByStage(hits, steps));
  const [waiting, setWaiting] = useState(new Set());
  const prevCols   = useRef(cols);
  const [dragCard, setDrag] = useState(null);
  const [overlaySize, setSize] = useState({});
  const [modalStage, setModal] = useState(null);
  const [showRejected, setShowRejected] = useState(false);

  // control movimiento y proyección
  const optimisticRef = useRef(null); // {activeId, from, to, index}
  const [inFlightMoves, setInFlightMoves] = useState(() => new Map());

  // overrides anti-rebote tras actualizar en server
  const [overrides, setOverrides] = useState(new Map());

  // aplicar overrides a hits
  const applyOverridesToHits = useCallback((hitList) => {
    if (!overrides.size) return hitList;
    return hitList.map(h => {
      const pid = String(h._source.id);
      const ov  = overrides.get(pid);
      return ov ? { ...h, _source: ov.data } : h;
    });
  }, [overrides]);

  useEffect(() => {
    if (!overrides.size) return;
    const id = setInterval(() => {
      setOverrides(prev => {
        const now = Date.now();
        let changed = false;
        const next = new Map();
        prev.forEach((val, key) => {
          if (now - val.ts < 30000) next.set(key, val);
          else changed = true;
        });
        return changed ? next : prev;
      });
    }, 2000);
    return () => clearInterval(id);
  }, [overrides.size]);

  // reagrupar cuando cambian hits (respetando orden y proyección)
  useEffect(() => {
    const merged = applyOverridesToHits(hits);
    const grouped = inFlightMoves.size
      ? groupByStageProjected(merged, steps, inFlightMoves)
      : groupByStage(merged, steps);
    setCols(prev => preserveOrder(prev, grouped));
  }, [hits, steps, applyOverridesToHits, inFlightMoves]);

  // helpers varios
  const containerOf = id =>
    id && id.startsWith("col-")
      ? id.slice(4)
      : Object.keys(cols).find(c => (cols[c] || []).some(h => h._id === id)) || null;

  const indexOfIn = (columnItems, id) =>
    (columnItems || []).findIndex(h => h._id === id);

  const getDropTarget = over => {
    if (!over) return { to: null, index: -1 };
    const overId = over.id;
    if (typeof overId === "string" && overId.startsWith("col-")) {
      const to = overId.slice(4);
      return { to, index: (cols[to] || []).length };
    }
    const to = containerOf(overId);
    const idx = indexOfIn(cols[to] || [], overId);
    return { to, index: idx < 0 ? (cols[to] || []).length : idx };
  };

  const openForm = useCallback(hit => {
    let updatedHit = hit;
    if (overrides.size) {
      const pid = String(hit._source.id);
      const ov = overrides.get(pid);
      if (ov) {
        updatedHit = { ...hit, _source: ov.data };
      }
    }
    
    const st = stageOf(updatedHit._source.applicant.enrollment, steps);
    if (st) {
      setModal({ ...st, applicant: updatedHit._source.applicant });
    }
  }, [steps, overrides]);

  // debounce para no traer snapshot viejo
  const reloadDebouncedRef = useRef(null);
  const safeReloadSearch = useCallback(() => {
    if (!reloadSearch) return;
    clearTimeout(reloadDebouncedRef.current);
    reloadDebouncedRef.current = setTimeout(() => {
      reloadSearch();
    }, 600);
  }, [reloadSearch]);

  // actualizar en sitio tras server OK
  const applyServerUpdate = useCallback((updated) => {
    const uid = String(updated.id);
    setOverrides(prev => {
      const next = new Map(prev);
      next.set(uid, { data: updated, ts: Date.now() });
      return next;
    });
    setCols(prev => {
      const out = {};
      for (const key of Object.keys(prev)) {
        out[key] = prev[key].map(h =>
          String(h._source.id) === uid ? { ...h, _source: updated } : h
        );
      }
      return out;
    });
  }, [steps]);

  // navegación a lista
  const goToList = postId => {
    switchToList?.();

    // Activa la pestaña "Postulaciones" si no está activa
    const tab = [...document.querySelectorAll(".nav-link")]
      .find(n => /postulaciones/i.test(n.textContent));
    dispatchClick(tab);

    let tries = 0;
    const seek = () => {
      const el = document.getElementById(`list-card-${postId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });

        // Reinicia la animación si ya tenía la clase
        el.classList.remove("flash-highlight");
        // Forzar reflow para reiniciar animación en todos los navegadores
        // eslint-disable-next-line no-unused-expressions
        void el.offsetWidth;
        el.classList.add("flash-highlight");

        // Mantén la clase un poco más que la animación por si hay jank
        setTimeout(() => el.classList.remove("flash-highlight"), 3200);
      } else if (++tries < 40) {
        // más intentos = más robusto si la lista demora en renderizar
        setTimeout(seek, 80);
      }
    };

    // pequeño delay para dar tiempo al cambio de vista
    setTimeout(seek, 80);
  };



  // colisión DnD
  const detectCollision = args => {
    const p = pointerWithin(args);   if (p.length) return p;
    const r = rectIntersection(args); if (r.length) return r;
    return closestCorners(args);
  };

  // DnD handlers
  const handleDragStart = ({ active }) => {
    const from = containerOf(active.id);
    prevCols.current = cols;
    setDrag(cols[from]?.find(h => h._id === active.id) || null);

    const node = active.data?.current?.sortable?.containerNode;
    if (node) {
      const { width, height } = node.getBoundingClientRect();
      setSize({ width, height });
    }
  };

  const moveItem = (prev, from, to, activeId, targetIndex) => {
    if (!from || !to) return prev;
    const src = prev[from] || [];
    const dst = prev[to]   || [];
    const curIdx = indexOfIn(src, activeId);
    if (curIdx < 0) return prev;
    const item = src[curIdx];

    if (from === to) {
      if (curIdx === targetIndex || curIdx === targetIndex - 1) return prev;
      const nextCol = [...src];
      nextCol.splice(curIdx, 1);
      nextCol.splice(targetIndex, 0, item);
      return { ...prev, [to]: nextCol };
    }

    const nextFrom = [...src];
    nextFrom.splice(curIdx, 1);

    const nextTo = [...dst];
    const safeIndex = Math.max(0, Math.min(targetIndex, nextTo.length));
    if (!nextTo.some(h => h._id === activeId)) {
      nextTo.splice(safeIndex, 0, item);
    }

    return { ...prev, [from]: nextFrom, [to]: nextTo };
  };

  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const from = containerOf(active.id);
    const { to, index } = getDropTarget(over);
    if (!to || to === ACCEPTED.type) return;

    // proyectar columna destino para evitar rebotes con hits externos
    setInFlightMoves(prev => {
      const next = new Map(prev);
      next.set(active.id, { to });
      return next;
    });

    const opt = optimisticRef.current;
    if (opt && opt.activeId === active.id && opt.to === to && opt.index === index) return;
    optimisticRef.current = { activeId: active.id, from, to, index };

    setCols(prev => moveItem(prev, from, to, active.id, index));
  };

  const revert = () => {
    setCols(prevCols.current);
    setInFlightMoves(prev => {
      if (!optimisticRef.current) return prev;
      const next = new Map(prev);
      next.delete(optimisticRef.current.activeId);
      return next;
    });
  };

  const handleDragEnd = async ({ active, over }) => {
    setDrag(null); setSize({});

    const { to: dest } = getDropTarget(over);
    const clearOptimistic = () => {
      const id = optimisticRef.current?.activeId;
      optimisticRef.current = null;
      setInFlightMoves(prev => {
        if (!id) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    };

    if (!dest || dest === ACCEPTED.type) { revert(); clearOptimistic(); return; }

    const moving = (cols[dest] || []).find(h => h._id === active.id);
    if (!moving) { revert(); clearOptimistic(); return; }

    const destStep = workFlow.find(s => s.type === dest);
    if (!canMove({ applicant:moving._source.applicant, targetStep:destStep, steps })) {
      showNotification?.("Ups", "No puedes saltar etapas requeridas", "warning");
      revert(); clearOptimistic(); return;
    }

    setWaiting(prev => new Set(prev).add(active.id));

    const fd  = new FormData(); fd.append("next_stage", dest);
    const rsp = await new Promise(r =>
      submitSingleNextStage(courseId, moving._source.id, fd, res => r(res))
    );

    setWaiting(prev => { const n = new Set(prev); n.delete(active.id); return n; });

    if (rsp.status !== 200) {
      revert();
      clearOptimistic();
      showNotification?.("Error", "No autorizado", "danger");
      return;
    }

    applyServerUpdate(rsp.data);

    boardRef?.current?.fetchSelections?.();
    showNotification?.("Listo", `Etapa cambiada a ${destStep.name}`, "success");
    safeReloadSearch();

    clearOptimistic();
  };

  return {
    // state
    steps, cols, waiting, dragCard, overlaySize, modalStage, showRejected,
    // setters
    setModal, setShowRejected,
    // dnd + logic
    sensors, detectCollision,
    handleDragStart, handleDragOver, handleDragEnd,
    // helpers
    openForm,
    applyServerUpdate,
    goToList
  };
}
